import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Layers } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { SparklineChart } from './shared/SparklineChart';
import { formatCurrencyCompact, formatCurrencyUnit, formatStat } from '../utils/formatters';

function PlatformCard({ name, color, summary, unifiedData, costKey }) {
  const [isOpen, setIsOpen] = useState(false);

  const spend = summary?.[costKey] || 0;
  const revenue = summary?.totalGoogleAdsRevenue || summary?.totalMetaRevenue || summary?.totalCriteoRevenue || 0;
  const roas = summary?.roas || 0;
  const cpc = summary?.cpc || 0;

  if (spend === 0 && revenue === 0) return null;

  // Extract sparkline data from unified data
  const sparkData = (unifiedData || [])
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map(d => d.ga4_total_revenue || d.conversion_value || 0);

  // Daily table data (latest 10 days)
  const dailyRows = (unifiedData || [])
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 10);

  return (
    <div className="glass-card rounded-2xl overflow-hidden" style={{ borderLeft: `4px solid ${color}` }}>
      {/* Collapsed header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-800">{name}</span>
          <div className="hidden sm:flex items-center gap-6 text-xs">
            <div><span className="text-gray-400">Spend</span> <span className="font-bold text-gray-800 ml-1">{formatCurrencyCompact(spend)}</span></div>
            <div><span className="text-gray-400">Revenue</span> <span className="font-bold text-gray-800 ml-1">{formatCurrencyCompact(revenue)}</span></div>
            <div><span className="text-gray-400">ROAS</span> <span className="font-bold text-gray-800 ml-1">{roas.toFixed(2)}x</span></div>
            <div><span className="text-gray-400">CPC</span> <span className="font-bold text-gray-800 ml-1">{formatCurrencyUnit(cpc)}</span></div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 hidden md:block">
            <SparklineChart data={sparkData} color={color} height={32} />
          </div>
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={16} className="text-gray-400" />
          </motion.div>
        </div>
      </button>

      {/* Expanded daily table */}
      <AnimatePresence>
        {isOpen && dailyRows.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-5 pb-4 max-h-[300px] overflow-y-auto">
              <table className="modern-table w-full">
                <thead>
                  <tr>
                    <th className="text-left">Date</th>
                    <th className="text-right">Spend</th>
                    <th className="text-right">Clicks</th>
                    <th className="text-right">Sessions</th>
                    <th className="text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.map((row, i) => {
                    const dateStr = typeof row.date === 'string' ? row.date.split('T')[0] : '';
                    const displayDate = dateStr ? new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                    return (
                      <tr key={i}>
                        <td className="text-xs text-gray-600">{displayDate}</td>
                        <td className="text-right text-xs font-medium">{formatCurrencyUnit(row.cost || row.spend || 0)}</td>
                        <td className="text-right text-xs">{formatStat(row.clicks || 0)}</td>
                        <td className="text-right text-xs">{formatStat(row.ga4_sessions || 0)}</td>
                        <td className="text-right text-xs font-medium">{formatCurrencyUnit(row.ga4_total_revenue || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function PlatformDeepDive({ googleAdsSummary, metaAdsSummary, criteoAdsSummary, googleAdsUnifiedData, metaAdsUnifiedData }) {
  return (
    <div className="mb-6">
      <SectionHeader title="Platform Deep Dive" icon={Layers} />
      <div className="space-y-3">
        <PlatformCard
          name="Google Ads"
          color="#3B82F6"
          summary={googleAdsSummary}
          unifiedData={googleAdsUnifiedData}
          costKey="totalCost"
        />
        <PlatformCard
          name="Meta Ads"
          color="#6366F1"
          summary={metaAdsSummary}
          unifiedData={metaAdsUnifiedData}
          costKey="totalSpend"
        />
        <PlatformCard
          name="Criteo"
          color="#F59E0B"
          summary={criteoAdsSummary}
          unifiedData={null}
          costKey="totalCost"
        />
      </div>
    </div>
  );
}
